import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { Alert, AlertSeverity } from '../types';
import { DateFilter, DateRange } from '../components/DateFilter';
import { 
  AlertTriangle, 
  Check, 
  Clock, 
  Filter, 
  Eye, 
  ExternalLink,
  Shield, 
  Users, 
  Brain,
  RefreshCw,
  PieChart as PieChartIcon,
  Activity
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  PieChart, 
  Pie, 
  Cell, 
  ResponsiveContainer,
  Tooltip,
  Legend
} from 'recharts';
import { Pagination } from '../components/Pagination';
import { formatSafeRelativeTime, formatAbsoluteTime } from '../utils/date-formatter';

const THREAT_COLORS = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#eab308',
  low: '#22c55e',
  unknown: '#6b7280'
};

export function Alerts() {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [totalAlerts, setTotalAlerts] = useState(0);
  const [unacknowledgedTotal, setUnacknowledgedTotal] = useState(0);
  const [criticalCount, setCriticalCount] = useState(0);
  const [highCount, setHighCount] = useState(0);
  const [mediumCount, setMediumCount] = useState(0);
  const [lowCount, setLowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [severityFilter, setSeverityFilter] = useState<string>('');
  const [acknowledgedFilter, setAcknowledgedFilter] = useState<string>('');
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: null,
    endDate: null,
    period: 'all'
  });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    loadAlerts();
  }, [severityFilter, acknowledgedFilter, searchParams, dateRange, pagination.page]);

  // Handle highlighting and scrolling to specific alert
  useEffect(() => {
    const highlightId = searchParams.get('highlight');
    if (highlightId && alerts.length > 0) {
      const element = document.getElementById(`alert-${highlightId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [alerts, searchParams]);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      const postId = searchParams.get('post_id');
      
      // Prepare date parameters
      const dateParams: any = {};
      if (dateRange.startDate) {
        dateParams.from_date = dateRange.startDate.toISOString();
      }
      if (dateRange.endDate) {
        dateParams.to_date = dateRange.endDate.toISOString();
      }
      
      // For "all time", don't send date parameters
      const apiDateParams = dateRange.period === 'all' ? {} : dateParams;
      
      const response = await api.getAlerts({
        severity: severityFilter || undefined,
        acknowledged: acknowledgedFilter ? acknowledgedFilter === 'true' : undefined,
        post_id: postId || undefined,
        page: pagination.page,
        limit: pagination.limit,
        ...apiDateParams
      });
      
      setAlerts(response.alerts);
      setTotalAlerts(response.total || response.alerts.length);
      setPagination(prev => ({
        ...prev,
        total: response.total || response.alerts.length,
        pages: response.pagination?.pages || Math.ceil((response.total || response.alerts.length) / prev.limit)
      }));
      
      // Get unacknowledged count
      if (!acknowledgedFilter || acknowledgedFilter !== 'true') {
        const unackResponse = await api.getAlerts({
          severity: severityFilter || undefined,
          acknowledged: false,
          post_id: postId || undefined,
          limit: 1, // We only need the total count
          ...apiDateParams
        });
        setUnacknowledgedTotal(unackResponse.total || 0);
      } else {
        setUnacknowledgedTotal(0);
      }
      
      // Get severity counts with current filters
      const severities = ['critical', 'high', 'medium', 'low'];
      const severityCounts = await Promise.all(
        severities.map(async (severity) => {
          const resp = await api.getAlerts({
            severity,
            acknowledged: acknowledgedFilter ? acknowledgedFilter === 'true' : undefined,
            post_id: postId || undefined,
            limit: 1,
            ...apiDateParams
          });
          return { severity, count: resp.total || 0 };
        })
      );
      
      setCriticalCount(severityCounts.find(s => s.severity === 'critical')?.count || 0);
      setHighCount(severityCounts.find(s => s.severity === 'high')?.count || 0);
      setMediumCount(severityCounts.find(s => s.severity === 'medium')?.count || 0);
      setLowCount(severityCounts.find(s => s.severity === 'low')?.count || 0);
    } catch (error) {
      console.error('Failed to load alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (alertId: string) => {
    try {
      await api.acknowledgeAlert(alertId);
      setAlerts(alerts.map(alert => 
        alert.id === alertId ? { ...alert, acknowledged: true } : alert
      ));
    } catch (error) {
      console.error('Failed to acknowledge alert:', error);
    }
  };

  const handleViewAnalysis = (postId: string) => {
    // Navigate to analyzed posts page with the specific post highlighted
    navigate(`/analyzed-posts?highlight=${postId}`);
  };

  // Process threat distribution from alert counts
  const threatDistribution = (() => {
    const distribution = [];
    if (criticalCount > 0) distribution.push({ name: 'critical', value: criticalCount, color: THREAT_COLORS.critical });
    if (highCount > 0) distribution.push({ name: 'high', value: highCount, color: THREAT_COLORS.high });
    if (mediumCount > 0) distribution.push({ name: 'medium', value: mediumCount, color: THREAT_COLORS.medium });
    if (lowCount > 0) distribution.push({ name: 'low', value: lowCount, color: THREAT_COLORS.low });
    return distribution;
  })();

  const getSeverityIcon = (severity: AlertSeverity) => {
    return <AlertTriangle className="h-5 w-5" />;
  };

  const getSeverityColor = (severity: AlertSeverity) => {
    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getSeverityBadgeClass = (severity: AlertSeverity) => {
    const baseClass = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ';
    switch (severity) {
      case 'critical': return baseClass + 'bg-red-100 text-red-800';
      case 'high': return baseClass + 'bg-orange-100 text-orange-800';
      case 'medium': return baseClass + 'bg-yellow-100 text-yellow-800';
      case 'low': return baseClass + 'bg-green-100 text-green-800';
      default: return baseClass + 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <div className="flex items-center space-x-3">
            <h2 className="text-2xl font-bold text-gray-900">Security Alerts</h2>
            <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
              {totalAlerts} total
            </span>
          </div>
          {(searchParams.get('post_id') || searchParams.get('highlight')) && (
            <div className="flex items-center space-x-2 mt-2">
              {searchParams.get('post_id') && (
                <div className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                  Filtered by Post ID: {searchParams.get('post_id')?.slice(0, 8)}...
                </div>
              )}
              {searchParams.get('highlight') && (
                <div className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                  Highlighting Alert: {searchParams.get('highlight')?.slice(0, 8)}...
                </div>
              )}
              <button
                onClick={() => navigate('/alerts')}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                Clear filter
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-4">
          <DateFilter 
            onChange={(newDateRange) => {
              setDateRange(newDateRange);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            initialValue={dateRange}
          />
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span className="text-sm text-gray-600">
                {totalAlerts} total alerts
              </span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              <span className="text-sm text-gray-600">
                {unacknowledgedTotal} unacknowledged
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="sm:w-48">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Severity
            </label>
            <select
              value={severityFilter}
              onChange={(e) => {
                setSeverityFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">All Severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          
          <div className="sm:w-48">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={acknowledgedFilter}
              onChange={(e) => {
                setAcknowledgedFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">All Alerts</option>
              <option value="false">Unacknowledged</option>
              <option value="true">Acknowledged</option>
            </select>
          </div>
        </div>
      </div>

      {/* Dashboard Section */}
      <div className="space-y-6">
        {/* Alert Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <AlertStatCard
            title="Total Alerts"
            value={totalAlerts}
            icon={Brain}
            color="purple"
            subtitle="Security alerts generated"
          />
          <AlertStatCard
            title="Critical Alerts"
            value={criticalCount}
            icon={AlertTriangle}
            color="red"
            subtitle="Immediate attention required"
          />
          <AlertStatCard
            title="High Priority"
            value={highCount}
            icon={Shield}
            color="orange"
            subtitle="Priority monitoring needed"
          />
          <AlertStatCard
            title="Unacknowledged"
            value={unacknowledgedTotal}
            icon={Clock}
            color="blue"
            subtitle="Pending review"
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Threat Distribution */}
          <div className="bg-white p-6 rounded-lg shadow-lg border">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Alert Severity Distribution</h3>
                <p className="text-sm text-gray-500">Breakdown by severity levels</p>
              </div>
              <PieChartIcon className="h-6 w-6 text-purple-500" />
            </div>
            {threatDistribution.length > 0 ? (
              <div style={{ width: '100%', height: 300 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={threatDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {threatDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value: number, name: string) => [
                        `${value} alerts`,
                        name.charAt(0).toUpperCase() + name.slice(1)
                      ]}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                No alert data available
              </div>
            )}
          </div>

          {/* Recent Alerts */}
          <div className="bg-white p-6 rounded-lg shadow-lg border">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Recent Alerts</h3>
                <p className="text-sm text-gray-500">Latest security alerts</p>
              </div>
              <Activity className="h-6 w-6 text-blue-500" />
            </div>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {alerts.slice(0, 8).map((alert, index) => (
                <div key={index} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50">
                  <div className={`w-3 h-3 rounded-full ${
                    alert.severity === 'critical' ? 'bg-red-500' :
                    alert.severity === 'high' ? 'bg-orange-500' :
                    alert.severity === 'medium' ? 'bg-yellow-500' :
                    'bg-green-500'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      @{alert.username || 'unknown'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatAbsoluteTime(alert.created_at)}
                    </p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                    alert.severity === 'critical' ? 'bg-red-100 text-red-800' :
                    alert.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                    alert.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {alert.severity.toUpperCase()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <hr className="border-gray-200" />

      {/* Alerts List */}
      <div className="bg-white shadow rounded-lg">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : alerts.length === 0 ? (
          <div className="text-center py-12">
            <AlertTriangle className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No alerts found</h3>
            <p className="mt-1 text-sm text-gray-500">
              No security alerts match your current filters.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {alerts.map((alert) => {
              const isHighlighted = searchParams.get('highlight') === alert.id;
              return (
                <div 
                  key={alert.id}
                  id={`alert-${alert.id}`}
                  className={`p-6 ${alert.acknowledged ? 'opacity-75' : ''} ${
                    isHighlighted ? 'ring-2 ring-purple-500 ring-opacity-50 bg-purple-50' : ''
                  }`}
                >
                <div className="flex items-start space-x-4">
                  <div className={`
                    flex-shrink-0 p-2 rounded-full border-2 
                    ${getSeverityColor(alert.severity)}
                  `}>
                    {getSeverityIcon(alert.severity)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <span className={getSeverityBadgeClass(alert.severity)}>
                          {alert.severity.toUpperCase()}
                        </span>
                        <span className="text-sm text-gray-500">
                          {alert.alert_type.replace('_', ' ').toUpperCase()}
                        </span>
                        <span className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                          {alert.id}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {alert.acknowledged ? (
                          <span className="inline-flex items-center text-sm text-green-600">
                            <Check className="h-4 w-4 mr-1" />
                            Acknowledged
                          </span>
                        ) : (
                          <button
                            onClick={() => handleAcknowledge(alert.id)}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Acknowledge
                          </button>
                        )}
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <p className="text-gray-900 font-medium">
                        {alert.message}
                      </p>
                      {alert.username && (
                        <p className="text-sm text-gray-600 mt-1">
                          From: @{alert.username}
                        </p>
                      )}
                      
                      {/* Source Information */}
                      <div className="mt-2 space-y-1">
                        {alert.data_source && (
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Data Source:</span> {alert.data_source}
                          </p>
                        )}
                        {(alert.intelligence_source_url || alert.post_url) && (
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Intelligence Source:</span>{' '}
                            <a 
                              href={alert.intelligence_source_url || alert.post_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 underline"
                            >
                              {alert.intelligence_source_username ? `@${alert.intelligence_source_username}` : 'View post'} on X
                            </a>
                          </p>
                        )}
                        
                        {/* View Analysis Link */}
                        {alert.analyzed_at && (
                          <div className="mt-2 pt-2 border-t border-gray-100">
                            <button
                              onClick={() => handleViewAnalysis(alert.post_id)}
                              className="inline-flex items-center text-sm text-purple-600 hover:text-purple-800 font-medium"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View Detailed Analysis
                              <ExternalLink className="h-3 w-3 ml-1" />
                            </button>
                            <p className="text-xs text-gray-500 mt-1">
                              Analyzed {new Date(alert.analyzed_at).toLocaleDateString()}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        {formatAbsoluteTime(alert.created_at)}
                      </div>
                      <span>Alert ID: {alert.id.slice(0, 8)}</span>
                    </div>
                    
                    {alert.content && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-md">
                        <p className="text-sm text-gray-700">
                          <strong>Content:</strong> {alert.content}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="bg-white rounded-lg shadow">
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.pages}
            onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
          />
        </div>
      )}

    </div>
  );
}

interface AlertStatCardProps {
  title: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  color: 'purple' | 'red' | 'orange' | 'blue';
  subtitle?: string;
}

function AlertStatCard({ title, value, icon: Icon, color, subtitle }: AlertStatCardProps) {
  const colorClasses = {
    purple: 'bg-purple-500 text-white',
    red: 'bg-red-500 text-white',
    orange: 'bg-orange-500 text-white',
    blue: 'bg-blue-500 text-white'
  };

  return (
    <div className="bg-white overflow-hidden shadow-lg rounded-lg border">
      <div className="p-6">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
              <Icon className="h-6 w-6" />
            </div>
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">
                {title}
              </dt>
              <dd className="text-2xl font-bold text-gray-900">
                {value.toLocaleString()}
              </dd>
              {subtitle && (
                <dt className="text-xs text-gray-400 mt-1">
                  {subtitle}
                </dt>
              )}
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}